/**
 * Match pending Sequence/card charges to purchased card POs so the inbox
 * does not ask cardholders to recategorize a purchase they already booked.
 */

import { isCardPurchasePayment } from './purchasingPolicy';
import { CARD_PO_AUTO_MATCH_AMOUNT_TOLERANCE, isAutoPurchasableCardPo } from './cardPoAutoPurchase';

export const CARD_PO_MATCH_TARGET_OVERHEAD = '__overhead__';
export const CARD_PO_MATCH_DAY_WINDOW = 7;

const VENDOR_STOP = new Set([
  'and',
  'the',
  'for',
  'from',
  'llc',
  'inc',
  'ltd',
  'purchase',
  'address',
  'com',
  'gosq',
  'www',
  'http',
  'https',
]);

export interface CardPoMatchExpense {
  id?: string;
  vendorName?: string | null;
  description?: string | null;
  totalAmount?: number | null;
  txnDate?: string | null;
  assignedUserId?: string | null;
  categorizationStatus?: string | null;
  purchaseRequisitionId?: string | null;
}

export interface CardPoMatchRequisition {
  id: string;
  poNumber?: string;
  status: string;
  vendorName?: string | null;
  title?: string | null;
  amount: number;
  purchasedAmount?: number | null;
  paymentMethodId?: string | null;
  paymentMethod?: string | null;
  requesterId?: string | null;
  purchasedById?: string | null;
  spendClass?: string | null;
  dealId?: string | null;
  plBucket?: string | null;
  costObject?: { type?: string; label?: string; spendClass?: string; dealId?: string; plBucket?: string };
  purchasedAt?: Date | null;
  approvedAt?: Date | null;
  createdAt?: Date | null;
  qbExpenseId?: string | null;
  qbExpenseIds?: string[] | null;
  prNumber?: string | null;
}

export interface CardPoMatchResult {
  requisition: CardPoMatchRequisition;
  target: string;
  dealTitle?: string;
  plBucket: 'overhead' | 'design' | 'timber' | 'production' | 'freight' | 'install' | 'travel' | 'unassigned';
}

export function vendorMatchTokens(text: string | null | undefined): string[] {
  return String(text || '')
    .toLowerCase()
    .replace(/purchase from/g, ' ')
    .replace(/\bsq\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2 && !VENDOR_STOP.has(w));
}

/** CRM/vendor display names that should match the same QuickBooks vendor. */
const VENDOR_ALIAS_PAIRS: Array<[string, string]> = [
  ['dan kiser', 'sojourn timber design'],
];

function expandVendorAliasHaystack(text: string | null | undefined): string {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const extras: string[] = [];
  for (const [left, right] of VENDOR_ALIAS_PAIRS) {
    if (lower.includes(left)) extras.push(right);
    if (lower.includes(right)) extras.push(left);
  }
  return extras.length ? `${raw} ${extras.join(' ')}` : raw;
}

export function vendorsOverlap(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = vendorMatchTokens(expandVendorAliasHaystack(a));
  const right = vendorMatchTokens(expandVendorAliasHaystack(b));
  if (left.length === 0 || right.length === 0) return false;
  const rightSet = new Set(right);
  const hits = left.filter(token => rightSet.has(token));
  if (hits.length >= 2) return true;
  if (hits.length === 1 && (left.length === 1 || right.length === 1)) return true;
  const joinedLeft = left.join(' ');
  const joinedRight = right.join(' ');
  return joinedLeft.includes(joinedRight) || joinedRight.includes(joinedLeft);
}

export function cardPoMatchAmount(requisition: CardPoMatchRequisition): number {
  if (typeof requisition.purchasedAmount === 'number' && Number.isFinite(requisition.purchasedAmount)) {
    return requisition.purchasedAmount;
  }
  return Number(requisition.amount) || 0;
}

export function amountsMatch(
  expenseAmount: number,
  requisition: CardPoMatchRequisition,
  tolerance = 0.01,
): boolean {
  return Math.abs((Number(expenseAmount) || 0) - cardPoMatchAmount(requisition)) <= tolerance;
}

function parseDay(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parts = raw.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysBetween(a: Date, b: Date): number {
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor(Math.abs(utcA - utcB) / 86400000);
}

export function datesWithinMatchWindow(expense: CardPoMatchExpense, requisition: CardPoMatchRequisition): boolean {
  const expenseDay = parseDay(expense.txnDate);
  if (!expenseDay) return false;
  const anchors = [requisition.purchasedAt, requisition.approvedAt, requisition.createdAt]
    .map(parseDay)
    .filter((d): d is Date => Boolean(d));
  if (anchors.length === 0) return false;
  return anchors.some(anchor => daysBetween(expenseDay, anchor) <= CARD_PO_MATCH_DAY_WINDOW);
}

export function isPurchasedCardPo(requisition: CardPoMatchRequisition): boolean {
  if (requisition.status !== 'purchased') return false;
  return isCardPurchasePayment({
    id: requisition.paymentMethodId || undefined,
    label: requisition.paymentMethod || undefined,
  });
}

export function peopleAlign(expense: CardPoMatchExpense, requisition: CardPoMatchRequisition): boolean {
  const assigned = (expense.assignedUserId || '').trim();
  if (!assigned) return false;
  return assigned === (requisition.requesterId || '').trim() || assigned === (requisition.purchasedById || '').trim();
}

export function categorizationFromPurchaseOrder(requisition: CardPoMatchRequisition): Omit<CardPoMatchResult, 'requisition'> {
  const dealId = (requisition.dealId || requisition.costObject?.dealId || '').trim();
  const spendClass = requisition.spendClass || requisition.costObject?.spendClass;
  const jobBucket = requisition.plBucket || requisition.costObject?.plBucket;
  if (spendClass === 'job' && dealId) {
    const bucket =
      jobBucket === 'design' ||
      jobBucket === 'timber' ||
      jobBucket === 'production' ||
      jobBucket === 'freight' ||
      jobBucket === 'install' ||
      jobBucket === 'travel'
        ? jobBucket
        : 'unassigned';
    return {
      target: dealId,
      dealTitle: requisition.costObject?.label || requisition.title || undefined,
      plBucket: bucket,
    };
  }
  return {
    target: CARD_PO_MATCH_TARGET_OVERHEAD,
    dealTitle: requisition.costObject?.label || undefined,
    plBucket: 'overhead',
  };
}

export const CARD_PO_PICKER_PREFIX = 'po:';

export function encodeCardPoPickerId(requisitionId: string): string {
  return `${CARD_PO_PICKER_PREFIX}${requisitionId}`;
}

export function parseCardPoPickerId(value: string | null | undefined): string | null {
  const raw = String(value || '').trim();
  if (!raw.startsWith(CARD_PO_PICKER_PREFIX)) return null;
  const id = raw.slice(CARD_PO_PICKER_PREFIX.length).trim();
  return id || null;
}

export function linkedQbExpenseIds(requisition: {
  qbExpenseId?: string | null;
  qbExpenseIds?: string[] | null;
}): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const value of [...(requisition.qbExpenseIds || []), requisition.qbExpenseId]) {
    const id = typeof value === 'string' ? value.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function isIssuedPurchaseOrderForPicker(row: {
  status: string;
  poNumber?: string | null;
}): boolean {
  return row.status === 'approved' || row.status === 'purchased' || Boolean(row.poNumber);
}

function formatPickerMoney(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export interface CardPoPickerOption {
  id: string;
  label: string;
  searchText: string;
}

export function purchaseOrderPickerOptions(
  rows: CardPoMatchRequisition[],
): CardPoPickerOption[] {
  return rows
    .filter(isIssuedPurchaseOrderForPicker)
    .slice()
    .sort((a, b) => {
      const aTime = (a.purchasedAt || a.approvedAt || a.createdAt)?.getTime() || 0;
      const bTime = (b.purchasedAt || b.approvedAt || b.createdAt)?.getTime() || 0;
      return bTime - aTime;
    })
    .map(row => {
      const num = row.poNumber || row.prNumber || 'PO';
      // Prefer the PO title so cardholders can tell same-vendor POs apart (CRM feedback #55).
      const detailBits = [row.title, row.vendorName]
        .map(s => String(s || '').trim())
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);
      const detail = detailBits.join(' · ') || 'Purchase';
      const amt = formatPickerMoney(cardPoMatchAmount(row));
      const linked = linkedQbExpenseIds(row).length;
      const extra = linked > 0 ? ` · ${linked} charge${linked === 1 ? '' : 's'}` : '';
      return {
        id: encodeCardPoPickerId(row.id),
        label: `${num} · ${detail} · ${amt}${extra}`,
        searchText: `${num} ${detail} ${row.title || ''} ${row.prNumber || ''}`.toLowerCase(),
      };
    });
}

/** Pin likely POs (vendor overlap) even when the charge is only part of the PO total. */
export function rankPurchaseOrdersForExpense(
  expense: CardPoMatchExpense,
  requisitions: CardPoMatchRequisition[],
): string[] {
  const expenseVendor = [expense.vendorName, expense.description].filter(Boolean).join(' ');
  const amount = Number(expense.totalAmount) || 0;
  return requisitions
    .filter(isIssuedPurchaseOrderForPicker)
    .map(row => {
      const poAmt = cardPoMatchAmount(row);
      const amountDelta = Math.abs(amount - poAmt);
      const overlap = vendorsOverlap(expenseVendor, [row.vendorName, row.title].filter(Boolean).join(' '));
      let score = 0;
      if (overlap) score += 50;
      if (peopleAlign(expense, row)) score += 10;
      if (amountDelta <= 0.01) score += 40;
      else if (poAmt > 0 && amount > 0 && amountDelta / poAmt <= 0.2) score += 15;
      else if (amount > 0 && poAmt > 0 && amount < poAmt) score += 8;
      if (row.status === 'purchased') score += 3;
      return { id: encodeCardPoPickerId(row.id), score };
    })
    .filter(row => row.score >= 50)
    .sort((a, b) => b.score - a.score)
    .map(row => row.id);
}

export function expenseAlreadyLinkedToPo(expense: CardPoMatchExpense, requisition: CardPoMatchRequisition): boolean {
  const linked = (expense.purchaseRequisitionId || '').trim();
  return Boolean(linked && linked === requisition.id);
}

export function purchaseOrderMatchesCardExpense(
  expense: CardPoMatchExpense,
  requisition: CardPoMatchRequisition,
): boolean {
  if (expense.categorizationStatus && expense.categorizationStatus !== 'pending') return false;
  const purchased = isPurchasedCardPo(requisition);
  const approvedCard = requisition.status === 'approved' && isAutoPurchasableCardPo(requisition);
  if (!purchased && !approvedCard) return false;
  const tolerance = approvedCard ? CARD_PO_AUTO_MATCH_AMOUNT_TOLERANCE : 0.01;
  if (!amountsMatch(Number(expense.totalAmount) || 0, requisition, tolerance)) return false;
  if (!purchased) {
    const expenseVendor = [expense.vendorName, expense.description].filter(Boolean).join(' ');
    const poVendor = [requisition.vendorName, requisition.title].filter(Boolean).join(' ');
    return vendorsOverlap(expenseVendor, poVendor);
  }
  if (!peopleAlign(expense, requisition)) return false;
  if (!datesWithinMatchWindow(expense, requisition)) return false;
  const expenseVendor = [expense.vendorName, expense.description].filter(Boolean).join(' ');
  const poVendor = [requisition.vendorName, requisition.title].filter(Boolean).join(' ');
  return vendorsOverlap(expenseVendor, poVendor);
}

/** Unique purchased card PO for this charge, or null if none / ambiguous. */
export function matchCardExpenseToPurchaseOrder(
  expense: CardPoMatchExpense,
  requisitions: CardPoMatchRequisition[],
): CardPoMatchResult | null {
  const matches = requisitions.filter(row => purchaseOrderMatchesCardExpense(expense, row));
  if (matches.length !== 1) return null;
  const requisition = matches[0];
  return {
    requisition,
    ...categorizationFromPurchaseOrder(requisition),
  };
}
